import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import io, { Socket } from 'socket.io-client';

type RefundTidRequest = number | { tid: number; isVan: boolean };

export const REFUND_SUCCEED_STATUS = {
  FAIL: 0,
  SUCCESS: 1,
  SUCCESS_LOG_FAILED: 2, // 환불 처리 완료 (VAN/현금 방출), DB 로그 기록 실패
} as const;

export type RefundSucceedStatus = (typeof REFUND_SUCCEED_STATUS)[keyof typeof REFUND_SUCCEED_STATUS];

const getSiteLink = window.location.href;
const logger = createLogger(LOG_PREFIX.SOCKET);

// Lazy 초기화를 위한 소켓 인스턴스
let socketInstance: Socket | null = null;

/**
 * 소켓 URL 반환
 */
const getSocketUrl = (): string => {
  return getSiteLink.includes('localhost')
    ? (process.env.REACT_APP_SOCKET_URL_DEV as string)
    : (process.env.REACT_APP_SOCKET_URL as string);
};

// 연결 완료 후 실행할 콜백
let onConnectCallback: (() => void) | null = null;

/**
 * 소켓 연결 초기화 (로그인 후 호출)
 */
export const connectSocket = (onConnect?: () => void): Socket => {
  // 이미 연결된 경우 콜백 즉시 실행
  if (socketInstance?.connected) {
    onConnect?.();
    return socketInstance;
  }

  // 이미 인스턴스가 있지만 연결 중인 경우 콜백만 등록
  if (socketInstance) {
    if (onConnect) {
      onConnectCallback = onConnect;
    }
    return socketInstance;
  }

  // 새 소켓 인스턴스 생성
  socketInstance = io(getSocketUrl(), {
    transports: ['websocket'],
  });

  // 연결 완료 시 콜백 실행
  if (onConnect) {
    onConnectCallback = onConnect;
  }

  socketInstance.on('connect', () => {
    if (onConnectCallback) {
      onConnectCallback();
      onConnectCallback = null;
    }
  });

  socketInstance.on('disconnect', () => {});

  socketInstance.on('connect_error', () => {});

  return socketInstance;
};

/**
 * 소켓 연결 해제 (로그아웃 시 호출)
 */
export const disconnectSocket = (): void => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};

/**
 * 소켓 인스턴스 반환 (연결되어 있지 않으면 null)
 */
export const getSocket = (): Socket | null => {
  return socketInstance;
};

/**
 * 소켓이 연결되어 있는지 확인
 */
export const isSocketConnected = (): boolean => {
  return socketInstance?.connected ?? false;
};

/**
 * 소켓 함수 호출(emit) 로그를 출력하는 헬퍼 함수
 */
const logSocketEmit = (functionName: string, params?: Record<string, any>) => {
  if (params) {
    logger.info(`${functionName} 실행`, params);
  } else {
    logger.info(`${functionName} 실행`);
  }
};

/**
 * 소켓 메시지 수신(on) 로그를 출력하는 헬퍼 함수
 */
export const logSocketReceive = (eventName: string, data?: Record<string, any>) => {
  if (data) {
    logger.info(`${eventName} 수신`, data);
  } else {
    logger.info(`${eventName} 수신`);
  }
};

/**
 * 소켓 emit 실행 (연결 확인 포함)
 */
const safeEmit = (event: string, ...args: any[]): boolean => {
  if (!socketInstance?.connected) {
    logger.warn(`소켓 이벤트 (${event}) 전송 실패: 연결되지 않음`);
    return false;
  }
  socketInstance.emit(event, ...args);
  return true;
};

export const joinRoom = (id: number) => {
  // logSocketEmit('joinRoom', { id });
  safeEmit('JoinSocketRoom', id);
};

export const openDoor = (id: number) => {
  logSocketEmit('openDoor', { id });
  safeEmit('request_openDoor', id);
};

export const openDoorWithLocation = (id: number, number: number, location: string) => {
  logSocketEmit('openDoorWithLocation', { id, number, location });
  safeEmit('request_openDoor_location', id, { number, location });
};

export const allOpenDoor = (id: number) => {
  logSocketEmit('allOpenDoor', { id });
  safeEmit('request_AllopenDoor', id);
};

export const payCancel = (id: number, tid: RefundTidRequest) => {
  logSocketEmit('payCancel', { id, tid });
  return safeEmit('refund_Product', id, tid);
};

export const payCancelInner = (id: number, tid: RefundTidRequest) => {
  logSocketEmit('payCancelInner', { id, tid });
  return safeEmit('refund_product_inner', id, tid);
};

/**
 * 환불 요청 (대상 위치 정보 포함)
 * @param merchantId
 * @param tid
 * @param location
 * @param number
 * @returns
 */
export const payCancelWithLocation = (
  merchantId: number,
  transactionId: number,
  isVan: boolean,
  location: string,
  number: number,
) => {
  logSocketEmit('payCancelWithLocation', {
    merchantId,
    transactionId,
    isVan,
    location,
    number,
  });
  return safeEmit('request_refund_location', merchantId, {
    merchantId,
    transactionId,
    isVan,
    location,
    number,
  });
};

export const recetipReprint = (id: number, rid: number) => {
  logSocketEmit('recetipReprint', { id, rid });
  safeEmit('reprint_Product', id, rid);
};

export const pRestart = (id: number) => {
  logSocketEmit('pRestart', { id });
  safeEmit('restart_program', id);
};

export const kRestart = (id: number) => {
  logSocketEmit('kRestart', { id });
  safeEmit('restart_kiosk', id);
};

export const innerPRestart = (id: number) => {
  logSocketEmit('innerPRestart', { id });
  safeEmit('inner_restart_program', id);
};

export const innerKRestart = (id: number) => {
  logSocketEmit('innerKRestart', { id });
  safeEmit('inner_restart_kiosk', id);
};

export const sendQrCode = (merchant: number, phoneNum: string, code: string) => {
  logSocketEmit('sendQrCode', { merchant, phoneNum, code });
  safeEmit('qr_print_from_backoffice', merchant, phoneNum, code);
};

// id: merchant, num: 자판기 아이템 숫자
export const vendingMachine = (id: number, num: number) => {
  logSocketEmit('vendingMachine', { id, num });
  safeEmit('request_vendingMachine', id, num);
};

export const healthCheckSocket = () => {
  logSocketEmit('healthCheckSocket');
  safeEmit('req_socket_connect');
};

// 멀티키오스크에 따른 문열림, 재시작
// 문열림
export const multiOpenDoor = (id: number, floor: number) => {
  logSocketEmit('multiOpenDoor', { id, floor });
  safeEmit('request_openDoor_inner', id, floor);
};

// PC 재시작
export const multiPcRestart = (id: number, floor: number) => {
  logSocketEmit('multiPcRestart', { id, floor });
  safeEmit('inner_restart_kiosk_Floor', id, floor);
};

// 프로그램 재시작
export const multiProgramRestart = (id: number, floor: number) => {
  logSocketEmit('multiProgramRestart', { id, floor });
  safeEmit('inner_restart_program_Floor', id, floor);
};

// 프로그램 업데이트
export const merchantUpdateKiosk = () => {
  logSocketEmit('merchantUpdateKiosk');
  safeEmit('request_AllupdateKiosk');
};

export const updateMerchantKioskV2 = () => {
  logSocketEmit('updateMerchantKioskV2');
  safeEmit('request_update_kiosk_all_v2');
};

export const outerUpdate = (id: number) => {
  logSocketEmit('outerUpdate', { id });
  safeEmit('request_updateKiosk_outer', id);
};

export const innerUpdate = (id: number) => {
  logSocketEmit('innerUpdate', { id });
  safeEmit('request_updateKiosk_inner', id);
};

/**
 * PC 재시작 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export const restartPCWithLocation = (merchantId: number, number: number, location: string) => {
  logSocketEmit('restartPCWithLocation', { merchantId, number, location });
  safeEmit('request_restart_kiosk_location', merchantId, { number, location });
};

/**
 * 프로그램 재시작 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export const restartProgramWithLocation = (
  merchantId: number,
  number: number,
  location: string,
) => {
  logSocketEmit('restartProgramWithLocation', { merchantId, number, location });
  safeEmit('request_restart_program_location', merchantId, { number, location });
};

/**
 * 키오스크 업데이트 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export const updateKioskWithLocation = (merchantId: number, number: number, location: string) => {
  logSocketEmit('updateKioskWithLocation', { merchantId, number, location });
  safeEmit('request_update_kiosk_location', merchantId, { number, location });
};
